<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("page.schedule.enumerate_fights_label") }}</template>
        <template v-slot:body>
            <div class="columns" v-if="initDone">
                <div class="column">
                    <div class="field">
                        <label class="label">{{ translate("page.schedule.enumerate_fights_label") }}:</label>
                        <div class="control">
                            <div>
                                <Radio name="enumerationType" :initial-value="2" v-model="enumerationType">
                                    {{ translate("page.schedule.fights_enumeration_type.globally_unique") }}
                                </Radio>
                            </div>
                            <div>
                                <Radio name="enumerationType" :initial-value="1" v-model="enumerationType">
                                    {{ translate("page.schedule.fights_enumeration_type.unique_per_mat") }}
                                </Radio>
                            </div>
                        </div>
                    </div>

                    <div>
                        <p>{{ translate("page.schedule.total_fights") }} {{ fightsEnumerationStatus.totalFights }}</p>
                        <p>
                            {{ translate("page.schedule.total_enumerated_fights") }}
                            {{ fightsEnumerationStatus.totalEnumeratedFights }}
                        </p>
                    </div>
                </div>
            </div>
            <div v-else class="columns is-centered is-mobile">
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.close") }}</button>
                <LoadingButton
                    :is-loading="enumerationInProgress"
                    class="button is-success"
                    @click="enumerateFights"
                    :disabled="enumerationBlocked"
                >
                    <span class="icon">
                        <i class="fa fa-list-ol" />
                    </span>
                    <span>{{ translate("page.schedule.enumerate_fights") }}</span>
                </LoadingButton>
                <LoadingButton class="button is-danger" @click="resetEnumeration" :is-loading="resetInProgress">
                    <span class="icon">
                        <i class="fa fa-undo" />
                    </span>
                    <span>{{ translate("common.reset") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed, watch } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import Radio from "../Common/Radio.vue";
    import localStorage from "../Common/store";
    import { translate } from "../Common/translations";
    import { useModal } from "../Common/useModal";
    import { handleError } from "../Common/errorHandler";
    import fightsApi, { FightsEnumeration, FightsEnumerationStatus, FightsEnumerationType } from "../Api/fights";

    const props = defineProps<{ eventId: number }>();

    const { modal, open: openModal, close } = useModal();
    const initDone = ref(false);
    const enumerationInProgress = ref(false);
    const enumerationType = ref<FightsEnumerationType>(FightsEnumeration.GlobalToEntireEvent);
    const fightsEnumerationStatus = ref<FightsEnumerationStatus>({
        totalFights: 0,
        totalEnumeratedFights: 0,
    });

    const enumerationBlocked = computed<boolean>(() => {
        return fightsEnumerationStatus.value.totalFights === fightsEnumerationStatus.value.totalEnumeratedFights;
    });

    watch(enumerationType, newVal => {
        saveEnumerationType(props.eventId, newVal);
    });

    function saveEnumerationType(eventId: number, enumerationTypeNewValue: number) {
        localStorage.set("fightsEnumerationType_" + eventId, enumerationTypeNewValue);
    }

    function loadEnumerationType(eventId: number): FightsEnumerationType {
        const value = localStorage.getInt("fightsEnumerationType_" + eventId, 2) ?? 2;

        if (value > 2) {
            return 2;
        }
        return value as FightsEnumerationType;
    }

    function open() {
        initDone.value = false;
        enumerationType.value = loadEnumerationType(props.eventId);

        openModal();

        refreshStatus()
            .catch(handleError)
            .finally(() => (initDone.value = true));
    }

    defineExpose({ open });

    function enumerateFights() {
        enumerationInProgress.value = true;
        saveEnumerationType(props.eventId, enumerationType.value);

        return fightsApi
            .enumerateFights(props.eventId, enumerationType.value)
            .then(() => {
                return refreshStatus();
            })
            .catch(handleError)
            .finally(() => (enumerationInProgress.value = false));
    }

    const resetInProgress = ref(false);

    function resetEnumeration(): Promise<void> {
        resetInProgress.value = true;
        return fightsApi
            .enumerateFights(props.eventId, FightsEnumeration.None)
            .then(() => {
                return refreshStatus();
            })
            .catch(handleError)
            .finally(() => {
                resetInProgress.value = false;
            });
    }

    function refreshStatus() {
        return fightsApi.getFightsEnumerationStatus(props.eventId).then(status => {
            fightsEnumerationStatus.value = status;
        });
    }
</script>

<style scoped></style>
